/**
 * Regional free shipping limit + initial message (Quebec vs rest of Canada) via IP geolocation.
 * Works with the existing free-shipping-bar.js: it sets data-limit / data-msg-initial, then calls update().
 */
(() => {
  'use strict';

  const CFG = window.freeShipping || {};
  const KEY = 'fsb_geo_v3';
  const TTL = 7 * 24 * 60 * 60 * 1000; // cache per visitor for a week
  const QC_CENTS = Math.round(Number(CFG.qc || 59) * 100);
  const OTHERS_CENTS = Math.round(Number(CFG.others || 99) * 100);
  const MSG_QC = CFG.msgInitialQc || 'Free shipping on orders over {{limit}} in Quebec!';
  const MSG_OTHERS = CFG.msgInitialOthers || 'Free shipping on orders over {{limit}} across Canada!';
  const TARGETS = '[data-free-shipping-bar], [data-free-shipping-product]';

  const norm = (s) => String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
  const read = () => { try { return JSON.parse(localStorage.getItem(KEY)); } catch (e) { return null; } };
  const write = (v) => { try { localStorage.setItem(KEY, JSON.stringify(v)); } catch (e) {} };

  const money = (cents) => {
    try {
      const cur = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'CAD';
      return new Intl.NumberFormat(document.documentElement.lang.startsWith('fr') ? 'fr-CA' : 'en-CA', {
        style: 'currency',
        currency: cur,
        minimumFractionDigits: cents % 100 ? 2 : 0,
        maximumFractionDigits: 2
      }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2).replace(/\.00$/, '');
    }
  };

  const fillTpl = (tpl, limit, isQC) =>
    String(tpl)
      .replace(/\{\{\s*limit\s*\}\}/g, money(limit))
      .replace(/\{\{\s*province\s*\}\}/g, isQC ? 'Quebec' : 'Canada');

  function apply(isQC) {
    const limit = isQC ? QC_CENTS : OTHERS_CENTS;
    const initialMsg = fillTpl(isQC ? MSG_QC : MSG_OTHERS, limit, isQC);

    document.querySelectorAll(TARGETS).forEach((el) => {
      el.dataset.limit = String(limit);          // read by free-shipping-bar.js
      el.dataset.msgInitial = initialMsg;        // empty-cart message, read by free-shipping-bar.js
      el.dataset.region = isQC ? 'qc' : 'default';
      el.classList.remove('free-shipping-bar--pending');
    });

    document.body.classList.toggle('quebec_state', isQC);

    if (window.FreeShippingBar && typeof window.FreeShippingBar.update === 'function') {
      window.FreeShippingBar.update();
    }
  }

  async function detect() {
    const cached = read();
    if (cached && Date.now() - cached.ts < TTL) return cached.isQC;
    if (!CFG.apiKey) return false;

    try {
      const ctrl = new AbortController();
      const t = setTimeout(() => ctrl.abort(), 3500);
      const res = await fetch(
        'https://api.ipgeolocation.io/ipgeo?apiKey=' + encodeURIComponent(CFG.apiKey),
        { signal: ctrl.signal }
      );
      clearTimeout(t);
      if (!res.ok) throw new Error('Geolocation request failed: ' + res.status);

      const geo = await res.json();
      const country = String(geo.country_code2 || '').toUpperCase();
      const code = String(geo.state_code || '').toUpperCase().split('-').pop(); // "QC" or "CA-QC"
      const isQC = country === 'CA' && (code === 'QC' || norm(geo.state_prov) === 'quebec');

      write({ isQC, ts: Date.now() });
      return isQC;
    } catch (e) {
      console.error('Free shipping: unable to detect visitor location', e);
      return false; // not cached, retries next visit; falls back to the rest-of-Canada message
    }
  }

  async function init() {
    if (!document.querySelector(TARGETS)) return;
    apply(await detect());
  }

  // Testing without a VPN: FreeShippingGeo.test(true) = Quebec, FreeShippingGeo.test(false) = elsewhere
  window.FreeShippingGeo = {
    test: apply,
    clearCache: () => { try { localStorage.removeItem(KEY); } catch (e) {} }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();